import { VuexModule, Module, Action, Mutation, MutationAction } from 'vuex-module-decorators'
import { deleteProfile, getProfiles, loadProfile, saveProfile } from '@/api/rodenstock'
import store from '@/store/index'
import ClientProfile, { IClientProfile } from '@/store/models/ClientProfile'
import ProfileModel from '@/store/models/ProfileModel'
import TilePayload from '@/store/models/TilePayload'
import Tile from '@/store/models/Tile'
import { SelectionType, Gender } from '@/data/enums'

export interface IMatrixState {
  name: string
  age: number
  clientProfiles: IClientProfile[]
  profile: ProfileModel
  activities: Tile[]
  genders: Tile[]
  glasses: Tile[]
  screens: Tile[]
  sports: Tile[]
  transport: Tile[]
}

@Module({
  store,
  dynamic: true,
  name: 'matrix',
  namespaced: true
})
class Matrix extends VuexModule implements IMatrixState {
  public name = ''
  public age: number = 16
  public activities: Tile[] = []
  public genders: Tile[] = []
  public glasses: Tile[] = []
  public clientProfiles: IClientProfile[] = []
  public screens: Tile[] = []
  public sports: Tile[] = []
  public transport: Tile[] = []

  public profile = new ProfileModel()

  public get getSelectionsByType () {
    return (type: SelectionType): Tile[] => {
      switch (type) {
        case SelectionType.Activity:
          return this.activities
        case SelectionType.Gender:
          return this.genders
        case SelectionType.Glasses:
          return this.glasses
        case SelectionType.Screen:
          return this.screens
        case SelectionType.Sport:
          return this.sports
        case SelectionType.Transport:
          return this.transport
        default:
          return []
      }
    }
  }

  public get profileModel () {
    return this.profile
  }

  public get adviceLenses () {
    return this.profile.getAdviceLenses()
  }

  public get adviceMultifocal () {
    return this.profile.getAdviceMultifocal()
  }

  public get adviceReadingGlasses () {
    return this.profile.getAdviceReadingGlasses()
  }

  public get adviceScreenGlasses () {
    return this.profile.getAdviceScreenGlasses()
  }

  public get adviceSportGlasses () {
    return this.profile.getAdviceSportGlasses()
  }

  public get adviceSunGlasses () {
    return this.profile.getAdviceSunGlasses()
  }

  public get gender () {
    return this.profile.getGender()
  }

  // ACTIONS
  @Action
  public changeSelection (payload: TilePayload) {
    const itemList = this.profile.changeSelection(payload)
  }

  @Action
  public async clearProfile (gender: Gender) {
    const profile = new ProfileModel()
    profile.gender = gender
    this.context.commit('SET_PROFILE', profile)
    return this.context.dispatch('init')
  }

  @Action
  public init () {
    this.SET_AGE(this.profile.getAge())
    this.SET_ACTIVITIES(this.profile.getItemList(SelectionType.Activity))
    this.SET_GENDERS(this.profile.getItemList(SelectionType.Gender))
    this.SET_GLASSES(this.profile.getItemList(SelectionType.Glasses))
    this.SET_SCREENS(this.profile.getItemList(SelectionType.Screen))
    this.SET_SPORTS(this.profile.getItemList(SelectionType.Sport))
    this.SET_TRANSPORT(this.profile.getItemList(SelectionType.Transport))
  }

  @Action({ rawError: true })
  public async deleteProfile (clientProfile: IClientProfile) {
    if (clientProfile.id) {
      await deleteProfile(clientProfile.id)
      const clientProfiles = this.context.rootState.matrix.clientProfiles
        .filter((profile: ClientProfile) => clientProfile.id !== profile.id)
      this.context.commit('SET_CLIENT_PROFILES', clientProfiles)
    }
  }

  @Action
  public async getProfiles () {
    try {
      const getProfilesResponse = await getProfiles(this.context.rootGetters.oCode)
      if (
        typeof getProfilesResponse.data === 'object' &&
        getProfilesResponse.data.hasOwnProperty('json') &&
        Array.isArray(getProfilesResponse.data.json)
      ) {
        const clientProfiles = getProfilesResponse.data.json
          .filter((profile: ClientProfile) => profile.version && profile.version === 2)
          .map((profile: ClientProfile) => new ClientProfile(profile.name, profile))
        this.context.commit('SET_CLIENT_PROFILES', clientProfiles)
      }
    } catch (err) {
      console.error(err)
    }
  }

  @Action
  public async loadProfile (clientProfile: IClientProfile) {
    const profile = new ProfileModel(clientProfile)
    this.context.commit('SET_PROFILE', profile)
    return this.context.dispatch('init')
  }

  @Action
  public async saveProfile (name?: string) {
    name = name || ''
    const clientProfile = new ClientProfile(name, undefined, this.context.getters.profileModel)
    await saveProfile(this.context.rootState.data.optician.id, clientProfile)
    return this.context.dispatch('getProfiles')
  }

  @Action
  public setActivities (activities: Tile[]) {
    this.SET_ACTIVITIES(activities)
  }

  @Action
  public setAge (age: number) {
    this.SET_AGE(age)
  }

  @Action
  public setGenders (payload: Tile[]) {
    this.SET_GENDERS(payload)
  }

  @Action
  public setGlasses (payload: Tile[]) {
    this.SET_GLASSES(payload)
  }

  @Action
  public setGender (gender: Gender) {
    this.SET_GENDER(gender)
  }

  @Action
  public setName (name: string) {
    this.SET_NAME(name)
  }

  @Action
  public setProfile (profile: ProfileModel) {
    this.SET_PROFILE(profile)
  }

  @Action
  public setClientProfiles (clientProfiles: IClientProfile[]) {
    this.SET_CLIENT_PROFILES(clientProfiles)
  }

  @Action
  public setScreens (screens: Tile[]) {
    this.SET_SCREENS(screens)
  }

  @Action
  public setSports (sports: Tile[]) {
    this.SET_SPORTS(sports)
  }

  @Action
  public setTransport (transport: Tile[]) {
    this.SET_TRANSPORT(transport)
  }

  // MUTATIONS
  @Mutation
  private SET_ACTIVITIES (activities: Tile[]) {
    this.activities = activities
  }

  @Mutation
  private SET_AGE (age: number) {
    this.age = age
    this.profile.setAge(age)
  }

  @Mutation
  private SET_CLIENT_PROFILES (clientProfiles: IClientProfile[]) {
    this.clientProfiles = clientProfiles
  }

  @Mutation
  private SET_GENDERS (genders: Tile[]) {
    this.genders = genders
  }

  @Mutation
  private SET_GLASSES (glasses: Tile[]) {
    this.glasses = glasses
  }

  @Mutation
  private SET_NAME (name: string) {
    this.name = name
  }

  @Mutation
  private SET_GENDER (gender: Gender) {
    this.profile.setGender(gender)
  }

  @Mutation
  private SET_PROFILE (profile: ProfileModel) {
    this.profile = profile
  }

  @Mutation
  private SET_SCREENS (screens: Tile[]) {
    this.screens = screens
  }

  @Mutation
  private SET_SPORTS (sports: Tile[]) {
    this.sports = sports
  }

  @Mutation
  private SET_TRANSPORT (transport: Tile[]) {
    this.transport = transport
  }
}

export default Matrix
